import { encode as base64Encode } from "base-64";
import { CircularProgress } from "@mui/material";
import { API, DATEFORMATS } from "../../../globals/globals";
import { useExportAlarmsMutation, useGetAlarmsQuery } from "../../../store/api/devices/deviceApiSlice";
import CustomDataGrid from "../../../components/Shared/Table/CustomDataGrid";
import { disconnectedDeviceColumns } from "../../../components/Shared/Table/Columns";
import { IEvent } from "../../../store/api/devices/types";
import { useEffect, useMemo, useRef, useState } from "react";
import { convertEpochToGMT, errorMessage, exportInCSV, getSortBy } from "../../../utils/helpers";
import { useStoreDispatch } from "../../../store/hooks";
import { setOpenToast, setPaginationModel } from "../../../store/helpers/helperReducers";
import { useSelector } from "react-redux";
import { paginationModelSelector } from "../../../store/helpers/helperSelectors";
import {
  GridEventListener,
  GridRowParams,
  GridSortModel,
} from "@mui/x-data-grid";
import EventDrawer from "../../../components/Drawers/EventDrawer";
import SearchIcon from "@mui/icons-material/Search";
import { useDebounce } from "../hooks";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../components/ui/table";
import { SortableHeader } from "../../../components/ui/sortable-header";
import { ServerTablePagination } from "../../../components/ui/server-table-pagination";
import { Input } from "../../../components/ui/input";
import { Button } from "../../../components/ui/button";
import { Icon } from "../../../components/Shared/Icon";
import { Loader2 } from "lucide-react";

interface DisconnectedDeviceProps {
  selectedGeoRegions: string[];
}

const columnHelper = createColumnHelper<IEvent>();

const disconnectedDeviceTableColumns = [
  columnHelper.accessor("hostName", {
    header: ({ column }) => <SortableHeader column={column}>Device</SortableHeader>,
    size: 215,
    cell: ({ row }) => {
      const valueToDisplay = row.original.hostName ?? (row.original as any).deviceName;
      return <span className="text-link">{valueToDisplay || ""}</span>;
    },
  }),
  columnHelper.accessor("geoRegionName", {
    header: ({ column }) => <SortableHeader column={column}>Region</SortableHeader>,
    size: 215,
  }),
  columnHelper.accessor("occuredDateAndTimeMs", {
    header: ({ column }) => <SortableHeader column={column}>Time Occured</SortableHeader>,
    size: 215,
    cell: ({ getValue }) => convertEpochToGMT(getValue(), DATEFORMATS.TS),
  }),
];

interface ToolbarProps {
  searchText: string;
  setSearchText: React.Dispatch<React.SetStateAction<string>>;
  exportHandler: () => void;
  exportLoading: boolean;
}

const DataGridToolbar: React.FC<ToolbarProps> = ({
  searchText,
  setSearchText,
  exportHandler,
  exportLoading,
}) => {
  return (
    <div className="flex items-center justify-between">
      <div className="relative w-80">
        <SearchIcon
          sx={{ fontSize: 18 }}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"
        />
        <input
          type="text"
          placeholder="Search devices..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-input rounded-md
                     focus:outline-none focus:ring-1 focus:ring-ring
                     focus:border-ring transition duration-200"
          style={{
            backgroundColor: "var(--card)",
            color: "var(--foreground)",
            fontSize: "0.875rem",
            height: 36,
          }}
        />
      </div>
      <Button
        variant="outline"
        size="icon"
        className="h-9 w-9 shrink-0"
        disabled={exportLoading}
        onClick={exportHandler}
        aria-label="Export CSV"
      >
        {exportLoading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Icon name="download" size={18} />
        )}
      </Button>
    </div>
  );
};

const DisconnectedDevice: React.FC<DisconnectedDeviceProps> = ({
  selectedGeoRegions,
}) => {
  const [exportAlarms, exportStatus] = useExportAlarmsMutation();
  const dispatch = useStoreDispatch();

  const [rowClicked, setRowClicked] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState<IEvent>();
  const [sorting, setSorting] = useState<SortingState>([{ id: "retention", desc: true }]);

  const [searchText, setSearchText] = useState("");
  //const debouncedSearch = useDebounce(searchText, 500);

  const paginationModel = useSelector(paginationModelSelector);
  const pagination = paginationModel.disconnectedDevicesAlarms || { page: 0, pageSize: 10 };
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Debounce search text — only update after 500ms of no typing
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchText);
      // Reset to first page on new search
      if (searchText !== debouncedSearch) {
        dispatch(
          setPaginationModel({
            disconnectedDevicesAlarms: { ...pagination, page: 0 },
          })
        );
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchText]);

  // Derive sortOrder string from TanStack sorting state
  const sortOrder = useMemo(() => {
    if (sorting.length > 0) {
      return `${sorting[0].id},${sorting[0].desc ? "desc" : "asc"}`;
    }
    return "retention,desc";
  }, [sorting]);

  const handleCloseButtonClicked = (value: boolean) => {
    setRowClicked(value);
  };

  const handleRowClicked = (params: GridRowParams<IEvent>) => {
    setRowClicked(true);
    setSelectedRowData(params.row);
  };

  const handlePageChange = (newPage: number) => {
    dispatch(
      setPaginationModel({
        disconnectedDevicesAlarms: { ...pagination, page: newPage },
      })
    );
  };

  const args = useMemo(() => {
    const predicate = {
      type: "AND",
      operator: "",
      fieldName: "",
      value: "",
      predicates: [
        {
          type: "",
          operator: "EQUAL",
          fieldName: "eventtype",
          value: "deviceDisconnected",
          predicates: [{}],
        },
        ...(selectedGeoRegions.length > 0
          ? [
            {
              type: "",
              operator: "IN",
              fieldName: "geoRegionId",
              value: selectedGeoRegions.join(","),
              predicates: [{}],
            },
          ]
          : []),
      ],
    };

    const params = new URLSearchParams();
    const base64EncodedStr = base64Encode(JSON.stringify(predicate));

    params.set("filters", base64EncodedStr);
    params.set("search", debouncedSearch || debouncedSearch);
    params.set("sort", sortOrder);
    params.set("page", pagination.page.toString());
    params.set("size", pagination.pageSize.toString());

    return { filters: params.toString() };
  }, [
    debouncedSearch,
    debouncedSearch, selectedGeoRegions,
    sortOrder,
    pagination.page,
    pagination.pageSize,
  ]);

  const { data, isLoading } = useGetAlarmsQuery(args, {
    pollingInterval:
      API.ENDPOINTS.ALARMS.pollingInterval || API.POLLING_INTERVAL,
  });

  const rows = data?.data || [];
  const totalCount = data?.totalItems || 0;

  // const table = useReactTable({
  //   data: rowsData,
  //   columns: disconnectedDeviceTableColumns,
  //   state: { sorting },
  //   onSortingChange: setSorting,
  //   getCoreRowModel: getCoreRowModel(),
  //   manualPagination: true,
  //   manualSorting: true,
  //   pageCount: Math.ceil(noOfRows / pagination.pageSize),
  // });

  const exportHandler = async () => {
    try {
      const response: any = await exportAlarms(args);
      if (response?.error) throw response?.error;
      exportInCSV(response, "Disconnected_Devices");
    } catch (error: any) {
      dispatch(
        setOpenToast({
          openToast: true,
          toastType: "error",
          message: errorMessage(error),
        })
      );
    }
  };

  return (
    <>
      {rowClicked && <EventDrawer data={selectedRowData} setRowClicked={setRowClicked} closedButtonClicked={handleCloseButtonClicked} isAlarmContext={true} />}
      <div className="w-full">
        {isLoading ? (
          <div className="flex justify-center items-center min-h-[200px]">
            <CircularProgress size={24} />
          </div>
        ) : (
          <>
            <h4 className="text-2xl font-bold my-0" style={{ color: "var(--foreground)" }}>{totalCount}</h4>

            <div className="mt-4 space-y-4">
              <DataGridToolbar
                searchText={searchText}
                setSearchText={setSearchText}
                exportHandler={exportHandler}
                exportLoading={exportStatus.isLoading}
              />

              <CustomDataGrid
                  tableId="disconnectedDevicesAlarms"
                  rows={rows}
                  columns={disconnectedDeviceColumns}
                  rowCount={totalCount}
                  loading={isLoading}
                  disableColumnMenu
                  getRowId={(row: IEvent) => row.objectKey}
                  paginationMode="server"
                  onRowClick={handleRowClicked}
                  onSortModelChange={(params: GridSortModel) =>
                    setSorting(
                      params.map((sort) => ({
                        id: sort.field,
                        desc: sort.sort === "desc",
                      }))
                    )
                  }
                  sx={{ height: 600 }}
                />
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default DisconnectedDevice;
